import React, { type FunctionComponent, useState, useMemo, useEffect, useCallback } from 'react'
import { type DashRouteComponentProps } from '@retailer-platform/dashboard/routing'
import snakeCase from 'lodash/snakeCase'
import { DocumentTitle } from '@retailer-platform/dashboard/gin-and-tonic'
import {
  Alert,
  borderRadius,
  Breadcrumb,
  Breadcrumbs,
  Button,
  colors,
  LoadingDots,
  NewSelect,
  NotificationLevel,
  notify,
  PageHeader,
  PageLayout,
  PageLayoutContent,
  spacing,
  Tab,
  TabList,
  TabPanel,
  Tabs,
  Text,
} from '@retailer-platform/shared-components'
import { AddIcon, CloseIcon, EditIcon, SearchIcon } from '@instacart/ids-core'
import { startCase } from 'lodash'
import styled from '@emotion/styled'
import { css } from '@emotion/react'
import { useUrlSearchParams } from '@retailer-platform/dashboard/utils'
import { useHistory } from 'react-router'
import kebabCase from 'lodash/kebabCase'
import { useDomainGoToPath, useDomainScopedPathname } from '../../utils/domain/routing.ts'
import { useDomainMessages } from '../../utils/domain/intl.ts'
import {
  type AuditWorkflow,
  useGetAuditWorkflowConfigsQuery,
  useListAuditWorkflowsQuery,
  useUpdateAuditWorkflowMutation,
} from '../../api'
import {
  QAS_CLIENT,
  type QualityAuditWorkflowColumnConfig,
  type QualityAuditWorkflowFeatureConfig,
} from '../../types/qualityAuditWorkflows.types.ts'
import { Input } from '../../components/md-platform-list/components/MdPlatformModifyModal.tsx'
import {
  Property,
  PropertySection,
  PropertyTitle,
  Row,
  Spacer,
} from '../../utils/common.styles.tsx'
import { QualityAuditWorkflowConfigColumnModal } from '../../components/quality-audit-workflow-config-column-modal/QualityAuditWorkflowConfigColumnModal.tsx'
import { getWorkflowConfig } from '../../utils/qas/workflow-configs/workflowConfigs.ts'
import { QualityAuditWorkflowConfigFeatureModal } from '../../components/quality-audit-workflow-config-feature-modal/QualityAuditWorkflowConfigColumnModal.tsx'
import { QualityAuditWorkflowTombstoneConfirmModal } from '../../components/quality-audit-workflow-tombstone-modal/QualityAuditWorkflowTombstoneModal'
import { useOutsourceVendor } from '../../utils/qas/useOutsourceVendor.tsx'
import { PropertyContainer } from '../../components/shared-styled-components/SharedStyledComponents.tsx'
import { QualityAuditWorkflowModal } from '../../components/quality-audit-workflow-modal/QualityAuditWorkflowTaskModal.tsx'
import { QualityAuditWorkflowConfigPresets } from '../../components/quality-audit-workflow-config-presets/QualityAuditWorkflowConfigPresets.tsx'

interface Props extends DashRouteComponentProps {}

const tabs = {
  settings: 'settings',
  fetures: 'features',
  columns: 'columns',
}

const tabStyle = css({
  height: '100%',
  width: '100%',
  overflow: 'auto',
  padding: 0,
})

export const VerticalDivider = styled.div({
  height: '100%',
  width: '1px',
  marginRight: `10px`,
  marginLeft: `10px`,
  borderRight: `1px solid ${colors.GRAYSCALE.X30}`,
})

export const PropertyListContainer = styled.div({
  borderRadius: borderRadius.X8,
  display: 'flex',
  flexDirection: 'column',
  color: colors.GRAYSCALE.X70,
  gap: spacing.X12,
  height: '100%',
  width: '100%',
  overflow: 'auto',
  border: `1px dashed ${colors.GRAYSCALE.X30}`,
  padding: `${spacing.X12}`,
})

export const PropertyListElement = styled.div<{ variant?: 'transparent' | 'default' }>(
  ({ variant }) => ({
    justifyContent: 'left',
    display: 'flex',
    alignItems: 'center',
    border: `1px solid ${colors.GRAYSCALE.X30}`,
    background: variant === 'transparent' ? 'transparent' : `${colors.GRAYSCALE.X10}`,
    borderRadius: borderRadius.X8,
    padding: spacing.X8,
    gap: `${spacing.X20}`,
    minWidth: 'fit-content',
  })
)

export const cssPropertyListElementButton = css({
  justifyItems: 'center',
  alignItems: 'center',
  margin: 0,
  height: '100%',
})

const CatalogAdminQualityAuditWorkflowModifyPage: FunctionComponent<Props> = () => {
  const history = useHistory()
  const { value: searchParamWorkflowUrl } = useUrlSearchParams('workflowName')
  const [columnsConfigState, setColumnConfigsState] = useState<QualityAuditWorkflowColumnConfig[]>(
    []
  )
  const [featureConfigMapState, setFeatureConfigMapState] =
    useState<QualityAuditWorkflowFeatureConfig>({})
  const [workflowState, setWorkflowState] = useState<AuditWorkflow | undefined>(undefined)
  const [workflowName, setWorkflowName] = useState<string | undefined>(undefined)
  const [columnModalState, setColumnModalState] = useState<
    { index: number; config: QualityAuditWorkflowColumnConfig } | undefined
  >(undefined)
  const [featureModalState, setFeatureModalState] = useState<
    { name: string; value: string } | undefined
  >(undefined)
  const [showWorkflowModal, setShowWorkflowModal] = useState<boolean>(false)
  const [isTombstoned, setIsTombstoned] = useState<boolean>(false)
  const [showTombstoneConfirmModal, setShowTombstoneConfirmModal] = useState(false)
  const handleTombstoneConfirm = () => {
    setIsTombstoned(true)
    setShowTombstoneConfirmModal(false)
  }

  useEffect(() => {
    if (!searchParamWorkflowUrl) return
    setWorkflowName(decodeURIComponent(searchParamWorkflowUrl))
  }, [searchParamWorkflowUrl])

  useEffect(() => {
    if (!workflowName) return
    const queryParams = new URLSearchParams({
      workflowName: encodeURIComponent(workflowName ?? ''),
    })
    history.push({ search: queryParams.toString() })
  }, [workflowName, history, searchParamWorkflowUrl])

  const listPath = useDomainScopedPathname('app-admin-catalog-admin-quality-audit-workflow-list')
  const modifyPath = useDomainScopedPathname(
    'app-admin-catalog-admin-quality-audit-workflow-modify'
  )
  const gotoWorkflowListPage = useDomainGoToPath(
    'app-admin-catalog-admin-quality-audit-workflow-list'
  )
  const goToWorkflowConfigPage = useDomainGoToPath(
    'app-admin-catalog-admin-quality-audit-workflow-modify',
    undefined,
    undefined,
    {
      workflowName: encodeURIComponent(workflowName ?? ''),
    }
  )
  const handleExportWorkflow = () => {
    const blob = new Blob(
      [
        JSON.stringify({
          columns: columnsConfigState,
          features: featureConfigMapState,
        }),
      ],
      { type: 'text/json' }
    )
    const link = document.createElement('a')

    link.download = `${snakeCase(workflowName)}_config.json`
    link.href = window.URL.createObjectURL(blob)
    link.dataset.downloadurl = ['text/json', link.download, link.href].join(':')

    const evt = new MouseEvent('click', {
      view: window,
      bubbles: true,
      cancelable: true,
    })

    link.dispatchEvent(evt)
    link.remove()
  }
  const handleImportWorkflow = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) return
    const reader = new FileReader()
    reader.onload = () => {
      try {
        const text = reader.result as string
        const json = JSON.parse(text)

        const columns = json?.['columns']
        const features = json?.['features']

        if (features && columns) {
          setColumnConfigsState(columns)
          setFeatureConfigMapState(features)
        } else {
          notify({
            contents: i18n.importWorkflowFailure,
            level: NotificationLevel.Error,
          })
        }
      } catch (error) {
        notify({
          contents: i18n.importWorkflowFailure,
          level: NotificationLevel.Error,
        })
      }
    }

    reader.readAsText(file)
  }

  const i18n = useDomainMessages({
    pageTitleText: 'catalogAdminDomain.qualityAuditWorkflows.modify.title',
    selectWorkflow: 'catalogAdminDomain.qualityAuditWorkflows.modify.selectWorkflow',
    changeName: 'catalogAdminDomain.qualityAuditWorkflows.modify.changeName',
    samplePercentageTitle: 'catalogAdminDomain.qualityAuditWorkflows.modify.samplePercentage.title',
    samplePercentageDetails:
      'catalogAdminDomain.qualityAuditWorkflows.modify.samplePercentage.details',
    failurePercentageTitle:
      'catalogAdminDomain.qualityAuditWorkflows.modify.failurePercentage.title',
    failurePercentageDetails:
      'catalogAdminDomain.qualityAuditWorkflows.modify.failurePercentage.details',
    settingsTitle: 'catalogAdminDomain.qualityAuditWorkflows.modify.settings.title',
    featuresTitle: 'catalogAdminDomain.qualityAuditWorkflows.modify.features.title',
    featuresDetails: 'catalogAdminDomain.qualityAuditWorkflows.modify.features.details',
    slaDaysTitle: 'catalogAdminDomain.qualityAuditWorkflows.modify.slaDays.title',
    slaDaysDetails: 'catalogAdminDomain.qualityAuditWorkflows.modify.slaDays.details',
    columnsTitle: 'catalogAdminDomain.qualityAuditWorkflows.modify.columns.title',
    import: 'catalogAdminDomain.qualityAuditWorkflows.modify.import',
    export: 'catalogAdminDomain.qualityAuditWorkflows.modify.export',
    columnsDetails: 'catalogAdminDomain.qualityAuditWorkflows.modify.columns.details',
    workflowTitleText: 'catalogAdminDomain.qualityAuditWorkflows.list.title',
    myWorkflows: 'catalogAdminDomain.qualityAuditWorkflows.list.table.myWorkflows.title',
    notifyFetchErrorMsg: 'catalogAdminDomain.qualityAuditWorkflows.tasks.notifyFetchErrorMsg',
    save: 'catalogAdminDomain.qualityAuditWorkflows.configs.modal.save',
    close: 'catalogAdminDomain.qualityAuditWorkflows.configs.modal.close',
    legacyWorkflow: 'catalogAdminDomain.qualityAuditWorkflows.task.error.legacyWorkflow',
    tombstone: 'catalogAdminDomain.qualityAuditWorkflows.configs.modal.tombstone',
    domainLabel: 'catalogAdminDomain.qualityAuditWorkflows.createWorkflows.submit.form.name.domain',
    domainDetails:
      'catalogAdminDomain.qualityAuditWorkflows.createWorkflows.submit.form.name.domainDetails',
    serviceLabel:
      'catalogAdminDomain.qualityAuditWorkflows.createWorkflows.submit.form.name.service',
    serviceDetails:
      'catalogAdminDomain.qualityAuditWorkflows.createWorkflows.submit.form.name.serviceDetails',
    workflowTypeLabel:
      'catalogAdminDomain.qualityAuditWorkflows.createWorkflows.submit.form.name.workflow',
    workflowTypeDetails:
      'catalogAdminDomain.qualityAuditWorkflows.createWorkflows.submit.form.name.workflowDetails',
    importWorkflowSuccess:
      'catalogAdminDomain.qualityAuditWorkflows.createWorkflows.submit.form.name.importWorkflowSuccess',
    importWorkflowFailure:
      'catalogAdminDomain.qualityAuditWorkflows.createWorkflows.submit.form.name.importWorkflowFailure',
  })

  // If the user contains an outsource permission include it in the request.
  const outsourceVendor = useOutsourceVendor()

  const [triggerUpdateAuditWorkflow, { ...restUpdateAuditWorkflow }] =
    useUpdateAuditWorkflowMutation()

  const { data: apiListAuditWorkflows, ...listAuditWorkflowsQueryDataRest } =
    useListAuditWorkflowsQuery({
      variables: {
        client: QAS_CLIENT,
        returnTaskInformation: false,
        outsourceVendor,
      },
    })

  const workflowIdentifiers = (workflowName ?? '').split('/')
  const workflowL1 = workflowIdentifiers.length >= 1 ? workflowIdentifiers[0] : ''
  const workflowL2 = workflowIdentifiers.length >= 2 ? workflowIdentifiers[1] : ''
  const workflowL3 = workflowIdentifiers.length >= 3 ? workflowIdentifiers[2] : ''

  const [currentL1, setCurrentL1] = useState(workflowL1)
  const [currentL2, setCurrentL2] = useState(workflowL2)
  const [currentL3, setCurrentL3] = useState(workflowL3)

  const { data: getAuditWorkFlowColumnConfigsData, ...getAuditWorkFlowColumnConfigsDataRest } =
    useGetAuditWorkflowConfigsQuery({
      variables: {
        client: QAS_CLIENT,
        workflowL1,
        workflowL2,
        workflowL3,
        configType: 'columns',
      },
    })

  const { data: getAuditWorkFlowFeatureConfigsData, ...getAuditWorkFlowFeatureConfigsDataRest } =
    useGetAuditWorkflowConfigsQuery({
      variables: {
        client: QAS_CLIENT,
        workflowL1,
        workflowL2,
        workflowL3,
        configType: 'features',
      },
    })

  const workflows = useMemo(
    () => apiListAuditWorkflows?.demeterServiceListAuditWorkflows?.auditWorkflows ?? [],
    [apiListAuditWorkflows?.demeterServiceListAuditWorkflows?.auditWorkflows]
  )

  const featureConfigMap: QualityAuditWorkflowFeatureConfig = useMemo(() => {
    try {
      const configs =
        getAuditWorkFlowFeatureConfigsData?.demeterServiceGetAuditWorkflowConfigs?.configs ?? []
      if (configs.length > 0) return JSON.parse(configs[0].configValue ?? '{}')
      return {}
    } catch (e) {
      return []
    }
  }, [getAuditWorkFlowFeatureConfigsData?.demeterServiceGetAuditWorkflowConfigs?.configs])

  const columnConfigs: QualityAuditWorkflowColumnConfig[] = useMemo(() => {
    try {
      const configs =
        getAuditWorkFlowColumnConfigsData?.demeterServiceGetAuditWorkflowConfigs?.configs ?? []
      if (configs.length > 0) return JSON.parse(configs[0].configValue ?? '[]')
      return []
    } catch (e) {
      return []
    }
  }, [getAuditWorkFlowColumnConfigsData?.demeterServiceGetAuditWorkflowConfigs?.configs])

  const workflow = useMemo(() => {
    if ((workflowName ?? '').length === 0) return undefined

    for (const workflow of workflows) {
      if (workflow.name === workflowName) {
        return workflow
      }
    }
    return undefined
  }, [workflowName, workflows])

  useEffect(() => {
    if (!workflow) return
    setWorkflowState(workflow)
  }, [workflow])

  useEffect(() => {
    setCurrentL1(workflowL1)
    setCurrentL2(workflowL2)
    setCurrentL3(workflowL3)
  }, [workflowL1, workflowL2, workflowL3])

  useEffect(() => {
    setColumnConfigsState(columnConfigs)
  }, [columnConfigs])

  useEffect(() => {
    setFeatureConfigMapState(featureConfigMap)
  }, [featureConfigMap])

  // Error if we are selecting a legacy workflow.
  // These workflows are expected to display in a specific way, and allowing users to change it
  // can cause issues for Ops, as this impacts sensitive workflows like Problem Tracker.
  const isLegacyWorkflow = useMemo(() => {
    if (!workflowName) return false
    const legacyWorkflowConfig = getWorkflowConfig(workflowName, false)
    return legacyWorkflowConfig?.name != 'general_workflow'
  }, [workflowName])

  useEffect(() => {
    if (
      !restUpdateAuditWorkflow.error &&
      restUpdateAuditWorkflow.called &&
      !restUpdateAuditWorkflow.loading
    ) {
      const newWorkflowName = `${currentL1}/${currentL2}/${currentL3}`

      // First refetch the workflow list
      listAuditWorkflowsQueryDataRest.refetch().then(() => {
        // Update both the URL and the selected workflow name
        setWorkflowName(newWorkflowName)

        const queryParams = new URLSearchParams({
          workflowName: encodeURIComponent(newWorkflowName),
        })
        history.push({ search: queryParams.toString() })

        // Reset the update mutation state
        restUpdateAuditWorkflow.reset()
      })
    }
  }, [
    goToWorkflowConfigPage,
    restUpdateAuditWorkflow,
    currentL1,
    currentL2,
    currentL3,
    history,
    listAuditWorkflowsQueryDataRest,
  ])

  const workFlowOptions = useMemo(
    () =>
      workflows
        .map(workflow => ({
          label: (workflow.name ?? '')
            .split('/')
            .map(text => startCase(text))
            .join('/'),
          value: workflow.name ?? '',
        }))
        .sort((workflowA, workflowB) =>
          (workflowA?.value ?? '').localeCompare(workflowB?.value ?? '')
        ),
    [workflows]
  )

  const isLoading =
    listAuditWorkflowsQueryDataRest?.loading ||
    getAuditWorkFlowColumnConfigsDataRest?.loading ||
    restUpdateAuditWorkflow?.loading ||
    getAuditWorkFlowFeatureConfigsDataRest?.loading
  const showProperties = !isLoading && (workflowName ?? '').length > 0

  const onColumnDeleteChange = (indexToRemove: number) => {
    const newColumnConfigs = columnsConfigState.filter((_, index) => index !== indexToRemove)
    setColumnConfigsState(newColumnConfigs)
  }

  const onColumnIndexChange = (newIndex: number, oldIndex: number) => {
    if (newIndex < 0 || newIndex >= columnsConfigState.length) return
    setColumnConfigsState(columnsConfigState => {
      const tempConfig = columnsConfigState[newIndex]
      columnsConfigState[newIndex] = columnsConfigState[oldIndex]
      columnsConfigState[oldIndex] = tempConfig
      return [...columnsConfigState]
    })
  }

  const onColumnModifyChange = (config: QualityAuditWorkflowColumnConfig, index: number) => {
    setColumnModalState({
      index,
      config,
    })
  }

  useEffect(() => {
    if (restUpdateAuditWorkflow.error) {
      notify({
        contents: i18n.notifyFetchErrorMsg,
        level: NotificationLevel.Error,
      })
    }
  }, [i18n.notifyFetchErrorMsg, restUpdateAuditWorkflow.error])

  const onSaveColumnConfig = (index: number, newColumnConfig: QualityAuditWorkflowColumnConfig) => {
    if (index < 0 || index === null || index === undefined) return
    setColumnConfigsState(prevState => {
      const newConfigsList = [...prevState]
      if (index < newConfigsList.length) {
        newConfigsList[index] = newColumnConfig
      } else {
        newConfigsList.push(newColumnConfig)
      }
      return newConfigsList
    })
    setColumnModalState(undefined)
  }

  const onColumnAddColumn = () => {
    setColumnModalState({
      index: columnsConfigState.length,
      config: {
        inputs: [],
        alias: '',
        type: '',
      },
    })
  }

  const onFeatureMapSelect = (featureName: string) => {
    let modalValue = featureConfigMapState[featureName]
    try {
      modalValue = featureConfigMapState[featureName]
    } catch (e) {
      // Ignore parsing issue, user will fix it if they want.
    }
    setFeatureModalState({
      name: featureName,
      value: modalValue,
    })
  }
  const onFeatureMapDelete = (featureName: string) => {
    delete featureConfigMapState[featureName]
    setFeatureConfigMapState({
      ...featureConfigMapState,
    })
  }

  const onFeatureMapSave = (featureName: string, featureValue: string) => {
    delete featureConfigMapState[featureModalState?.name ?? '']
    setFeatureConfigMapState({
      ...featureConfigMapState,
      [featureName.toLowerCase().trim()]: featureValue,
    })
    setFeatureModalState(undefined)
  }

  const onFeatureMapAdd = () => {
    setFeatureModalState({
      name: '',
      value: '',
    })
  }

  const onSubmit = useCallback(() => {
    if (!workflowState?.id) {
      notify({
        contents: 'Workflow ID is required',
        level: NotificationLevel.Error,
      })
      return
    }
    triggerUpdateAuditWorkflow({
      variables: {
        input: {
          auditWorkflow: {
            id: workflowState.id,
            approvedTaskCount: workflowState?.approvedTaskCount,
            auditableTaskCount: workflowState?.auditableTaskCount,
            defaultSamplePct: workflowState?.defaultSamplePct,
            defaultSlaDays: workflowState?.defaultSlaDays,
            name: workflowState?.name,
            failOverPct: workflowState?.failOverPct,
            firstPendingSlaDueDate: workflowState?.firstPendingSlaDueDate,
            rejectedTaskCount: workflowState?.rejectedTaskCount,
            correctnessThresholdPct: workflowState?.correctnessThresholdPct,
            taskReviewPct: workflowState?.taskReviewPct,
          },
          configs: [
            {
              workflowL1,
              workflowL2,
              workflowL3,
              configType: 'columns',
              configValue: JSON.stringify(columnsConfigState),
            },
            {
              workflowL1,
              workflowL2,
              workflowL3,
              configType: 'features',
              configValue: JSON.stringify(featureConfigMapState),
            },
          ],
          tombstone: isTombstoned,
        },
      },
    })
  }, [
    workflowState,
    triggerUpdateAuditWorkflow,
    workflowL1,
    workflowL2,
    workflowL3,
    columnsConfigState,
    featureConfigMapState,
    isTombstoned,
  ])

  useEffect(() => {
    if (isTombstoned && !showTombstoneConfirmModal) {
      onSubmit()
      // Wait for the mutation to complete before resetting state
      if (!restUpdateAuditWorkflow.loading && !restUpdateAuditWorkflow.error) {
        // First refetch the workflow list
        listAuditWorkflowsQueryDataRest.refetch().then(() => {
          // Reset all state values after successful tombstone
          setWorkflowName(undefined)
          setWorkflowState(undefined)
          setColumnConfigsState([])
          setFeatureConfigMapState({})
          setCurrentL1('')
          setCurrentL2('')
          setCurrentL3('')
          setIsTombstoned(false)

          // Clear the URL parameters
          history.push({ search: '' })
        })
      }
    }
  }, [
    isTombstoned,
    showTombstoneConfirmModal,
    onSubmit,
    history,
    listAuditWorkflowsQueryDataRest,
    restUpdateAuditWorkflow.loading,
    restUpdateAuditWorkflow.error,
  ])

  const isDisabled =
    workflowState == workflow &&
    columnConfigs == columnsConfigState &&
    featureConfigMap == featureConfigMapState

  return (
    <PageLayout noSidebar noFooter>
      <DocumentTitle titleId="catalogAdminDomain.qualityAuditWorkflows.nav.title" />
      {showWorkflowModal && (
        <QualityAuditWorkflowModal
          onClose={() => setShowWorkflowModal(false)}
          onSubmit={() => setShowWorkflowModal(false)}
          title={i18n.changeName}
          disabled={false}
          isLoading={false}
          contentStylesOverride={{
            minWidth: '30vw',
            width: '90vw',
            maxWidth: 'none',
          }}
        >
          <PropertyContainer style={{ width: '100%' }}>
            <PropertySection style={{ flexDirection: 'row', gap: spacing.X16, width: '100%' }}>
              <Property style={{ flex: 1 }}>
                <PropertyTitle>{i18n.domainLabel}</PropertyTitle>
                <Text size="small">{i18n.domainDetails}</Text>
                <Input
                  style={{ width: '100%' }}
                  error={false}
                  onChange={event => {
                    const newL1 = event.target.value.endsWith(' ')
                      ? event.target.value.slice(0, -1) + '-'
                      : kebabCase(event.target.value)
                    setCurrentL1(newL1)
                    setWorkflowState({
                      ...workflowState,
                      name: `${newL1}/${currentL2}/${currentL3}`,
                    })
                  }}
                  value={currentL1}
                />
              </Property>
              <Property style={{ flex: 1 }}>
                <PropertyTitle>{i18n.serviceLabel}</PropertyTitle>
                <Text size="small">{i18n.serviceDetails}</Text>
                <Input
                  style={{ width: '100%' }}
                  error={false}
                  onChange={event => {
                    const newL2 = event.target.value.endsWith(' ')
                      ? event.target.value.slice(0, -1) + '-'
                      : kebabCase(event.target.value)
                    setCurrentL2(newL2)
                    setWorkflowState({
                      ...workflowState,
                      name: `${currentL1}/${newL2}/${currentL3}`,
                    })
                  }}
                  value={currentL2}
                />
              </Property>
              <Property style={{ flex: 1 }}>
                <PropertyTitle>{i18n.workflowTypeLabel}</PropertyTitle>
                <Text size="small">{i18n.workflowTypeDetails}</Text>
                <Input
                  style={{ width: '100%', marginTop: 'auto' }}
                  error={false}
                  onChange={event => {
                    const newL3 = event.target.value.endsWith(' ')
                      ? event.target.value.slice(0, -1) + '-'
                      : kebabCase(event.target.value)
                    setCurrentL3(newL3)
                    setWorkflowState({
                      ...workflowState,
                      name: `${currentL1}/${currentL2}/${newL3}`,
                    })
                  }}
                  value={currentL3}
                />
              </Property>
            </PropertySection>
          </PropertyContainer>
        </QualityAuditWorkflowModal>
      )}
      <PageHeader
        addBottomPadding
        title={
          <Breadcrumbs>
            <Breadcrumb path={listPath}>{i18n.workflowTitleText}</Breadcrumb>
            <Breadcrumb path={modifyPath}>{i18n.pageTitleText}</Breadcrumb>
          </Breadcrumbs>
        }
      />
      {columnModalState && (
        <QualityAuditWorkflowConfigColumnModal
          columnConfig={columnModalState.config}
          onClose={() => setColumnModalState(undefined)}
          onSave={columnConfig => onSaveColumnConfig(columnModalState?.index, columnConfig)}
        />
      )}
      {featureModalState && (
        <QualityAuditWorkflowConfigFeatureModal
          featureConfig={featureModalState}
          onClose={() => setFeatureModalState(undefined)}
          onSave={(name, value) => onFeatureMapSave(name, value)}
        />
      )}
      <QualityAuditWorkflowTombstoneConfirmModal
        isOpen={showTombstoneConfirmModal}
        onClose={() => setShowTombstoneConfirmModal(false)}
        onConfirm={handleTombstoneConfirm}
      />
      <PageLayoutContent
        style={{
          padding: spacing.X20,
          paddingTop: 0,
        }}
      >
        <PropertyContainer style={{ width: '100%' }}>
          <PropertySection style={{ width: '100%' }}>
            <Property>
              <PropertyTitle>{i18n.selectWorkflow}</PropertyTitle>
              <PropertySection
                style={{ flexDirection: 'row', gap: spacing.X16, alignItems: 'center' }}
              >
                <NewSelect
                  icon={SearchIcon}
                  placeholder={'Select Workflow Type'}
                  onChange={value => setWorkflowName(value)}
                  options={workFlowOptions}
                  value={workflowName}
                  isLoading={isLoading}
                  isSearchable={true}
                />
                {workflowName && !isLegacyWorkflow && !isLoading && (
                  <Button
                    variant={'secondary'}
                    style={{ whiteSpace: 'nowrap' }}
                    onClick={() => setShowWorkflowModal(true)}
                  >
                    {i18n.changeName}
                  </Button>
                )}
              </PropertySection>
              {isLegacyWorkflow && <Alert variant={'danger'}>{i18n.legacyWorkflow}</Alert>}
            </Property>
          </PropertySection>
        </PropertyContainer>
        {isLoading && <LoadingDots height={20} centered />}
        {showProperties && !isLegacyWorkflow && (
          <>
            <PropertyContainer
              style={{ width: '100%', height: '100%', overflow: 'hidden', gap: `${spacing.X12}` }}
            >
              <PropertySection style={{ width: '50%' }}>
                <Tabs css={tabStyle}>
                  <TabList>
                    <Tab id="settings">{i18n.settingsTitle}</Tab>
                    <Tab id="features">{i18n.featuresTitle}</Tab>
                  </TabList>
                  <TabPanel tabId={tabs.settings} css={tabStyle}>
                    <PropertySection style={{ flexDirection: 'row' }}>
                      <Property style={{ flex: 1 }}>
                        <PropertyTitle>{i18n.samplePercentageTitle}</PropertyTitle>
                        <Text size="small">{i18n.samplePercentageDetails}</Text>
                        <Input
                          style={{ width: '100%', marginTop: 'auto' }}
                          error={false}
                          onChange={event =>
                            setWorkflowState({
                              ...workflowState,
                              defaultSamplePct: Math.max(
                                0,
                                Math.min(100, Number.parseInt(event?.target?.value ?? '0'))
                              ),
                            })
                          }
                          type={'number'}
                          value={workflowState?.defaultSamplePct ?? 0}
                        />
                      </Property>
                      <Property style={{ flex: 1 }}>
                        <PropertyTitle>{i18n.failurePercentageTitle}</PropertyTitle>
                        <Text size="small">{i18n.failurePercentageDetails}</Text>
                        <Input
                          style={{ width: '100%', marginTop: 'auto' }}
                          error={false}
                          onChange={event =>
                            setWorkflowState({
                              ...workflowState,
                              failOverPct: Math.max(
                                0,
                                Math.min(100, Number.parseInt(event?.target?.value ?? '0'))
                              ),
                            })
                          }
                          type={'number'}
                          value={workflowState?.failOverPct ?? 0}
                        />
                      </Property>
                    </PropertySection>
                    <PropertySection style={{ flexDirection: 'row' }}>
                      <Property style={{ flex: 1 }}>
                        <PropertyTitle>{i18n.slaDaysTitle}</PropertyTitle>
                        <Text size="small">{i18n.slaDaysDetails}</Text>
                        <Input
                          style={{ width: '100%', marginTop: 'auto' }}
                          error={false}
                          onChange={event =>
                            setWorkflowState({
                              ...workflowState,
                              defaultSlaDays: `${event?.target?.value ?? 0}`,
                            })
                          }
                          type={'number'}
                          value={Number.parseInt(workflowState?.defaultSlaDays ?? '0')}
                        />
                      </Property>
                      <Property style={{ flex: 1 }}>
                        <PropertyTitle>Correctness Threshold Percentage</PropertyTitle>
                        <Text size="small">Set threshold of correct tasks during review.</Text>
                        <Input
                          style={{ width: '100%', marginTop: 'auto' }}
                          error={false}
                          onChange={event =>
                            setWorkflowState({
                              ...workflowState,
                              correctnessThresholdPct: Math.max(
                                0,
                                Math.min(100, Number.parseInt(event?.target?.value ?? '0'))
                              ),
                            })
                          }
                          type={'number'}
                          value={workflowState?.correctnessThresholdPct ?? 0}
                        />
                      </Property>
                      <Property style={{ flex: 1 }}>
                        <PropertyTitle>Task Review Percentage</PropertyTitle>
                        <Text size="small">Set the percentage of tasks to review.</Text>
                        <Input
                          style={{ width: '100%', marginTop: 'auto' }}
                          error={false}
                          onChange={event =>
                            setWorkflowState({
                              ...workflowState,
                              taskReviewPct: Math.max(
                                0,
                                Math.min(100, Number.parseInt(event?.target?.value ?? '0'))
                              ),
                            })
                          }
                          type={'number'}
                          value={workflowState?.taskReviewPct ?? 0}
                        />
                      </Property>
                    </PropertySection>
                  </TabPanel>
                  <TabPanel tabId={tabs.fetures} css={tabStyle}>
                    <Property style={{ flex: 1, height: '100%', overflow: 'auto' }}>
                      <Row
                        style={{
                          alignItems: 'center',
                          gap: `${spacing.X8}`,
                        }}
                      >
                        <Text size="small">{i18n.featuresDetails}</Text>
                        <Spacer />
                        <QualityAuditWorkflowConfigPresets
                          onChange={features => {
                            if (!features) return
                            setFeatureConfigMapState({
                              ...featureConfigMapState,
                              ...features,
                            })
                          }}
                        />
                        <Button
                          variant={'tertiary'}
                          style={{ margin: 0 }}
                          compact
                          onClick={() => onFeatureMapAdd()}
                        >
                          <AddIcon size={18} color="systemSuccessRegular" />
                        </Button>
                      </Row>
                      <PropertyListContainer>
                        {Object.keys(featureConfigMapState).map((featureName, index) => (
                          <PropertyListElement
                            variant={'transparent'}
                            key={`${index}-${featureName}`}
                          >
                            <Button
                              variant={'transparent'}
                              onClick={() => onFeatureMapSelect(featureName)}
                            >
                              {featureName}
                            </Button>
                            <Spacer />
                            <Button
                              variant={'transparent'}
                              css={cssPropertyListElementButton}
                              onClick={() => onFeatureMapSelect(featureName)}
                            >
                              <EditIcon size={18} color="systemSuccessRegular" />
                            </Button>
                            <Button
                              variant={'transparent'}
                              compact={true}
                              css={cssPropertyListElementButton}
                              onClick={() => onFeatureMapDelete(featureName)}
                            >
                              <CloseIcon size={18} color="systemSuccessRegular" />
                            </Button>
                          </PropertyListElement>
                        ))}
                      </PropertyListContainer>
                    </Property>
                  </TabPanel>
                </Tabs>
              </PropertySection>
              <PropertySection style={{ width: '50%', height: '100%' }}>
                <Tabs css={tabStyle}>
                  <TabList>
                    <Tab id="columns">{i18n.columnsTitle}</Tab>
                  </TabList>
                  <TabPanel tabId={tabs.columns} css={tabStyle}>
                    <Property style={{ flex: '1', height: '100%' }}>
                      <Row
                        style={{
                          alignItems: 'center',
                        }}
                      >
                        <Text size="small">{i18n.columnsDetails}</Text>
                        <Spacer />
                        <Button
                          variant={'tertiary'}
                          style={{ margin: 0 }}
                          compact
                          onClick={() => onColumnAddColumn()}
                        >
                          <AddIcon size={18} color="systemSuccessRegular" />
                        </Button>
                      </Row>
                      <PropertyListContainer>
                        {columnsConfigState.map((columnConfig, index) => (
                          <PropertyListElement key={`${columnConfig?.alias ?? ''}`}>
                            <Input
                              style={{ width: '65px', background: 'white' }}
                              error={false}
                              onChange={event => {
                                if (event.target.value.length == 0) return
                                const oldIndex = index
                                let newIndex = Number.parseInt(event.target.value) - 1
                                if (oldIndex > newIndex) {
                                  newIndex += 2
                                } else {
                                  newIndex -= 2
                                }
                                onColumnIndexChange(newIndex, oldIndex)
                              }}
                              type={'number'}
                              value={index + 1}
                            />
                            {columnConfig?.alias}
                            <Spacer />
                            <Button
                              variant={'tertiary'}
                              css={cssPropertyListElementButton}
                              onClick={() => onColumnModifyChange(columnConfig, index)}
                            >
                              <EditIcon size={18} color="systemSuccessRegular" />
                            </Button>
                            <Button
                              variant={'tertiary'}
                              css={cssPropertyListElementButton}
                              onClick={() => onColumnDeleteChange(index)}
                            >
                              <CloseIcon size={18} color="systemSuccessRegular" />
                            </Button>
                          </PropertyListElement>
                        ))}
                      </PropertyListContainer>
                    </Property>
                  </TabPanel>
                </Tabs>
              </PropertySection>
            </PropertyContainer>
            <Row>
              <Button variant="detrimental" onClick={() => setShowTombstoneConfirmModal(true)}>
                {i18n.tombstone}
              </Button>
              <Spacer />
              <Button
                variant="secondary"
                onClick={() => {
                  document.getElementById('importConfig')?.click()
                }}
              >
                {i18n.import}
              </Button>
              <input
                id="importConfig"
                type="file"
                accept=".json"
                style={{ display: 'none' }}
                onChange={handleImportWorkflow}
              />
              <Button
                variant="secondary"
                onClick={() => handleExportWorkflow()}
                style={{ borderRight: `1px solid ${colors.GRAYSCALE.X30}` }}
              >
                {i18n.export}
              </Button>
              <VerticalDivider />
              <Button variant="secondary" onClick={gotoWorkflowListPage}>
                {i18n.close}
              </Button>
              <Button variant="primary" disabled={isDisabled} onClick={() => onSubmit()}>
                {i18n.save}
              </Button>
            </Row>
          </>
        )}
      </PageLayoutContent>
    </PageLayout>
  )
}

export default CatalogAdminQualityAuditWorkflowModifyPage
